import { Avatar, CollapseCard, ErrorBoundary } from '@dixa/lib-shared-habla';
import { colors, typography } from '@dixa/lib-shared-tokens';
import { useEffect } from 'react';

import { VisibilityToggle } from 'components/conversation/components/sidebar/components/visibilityToggle/visibilityToggle';
import { actions as personalActions } from 'components/settings/actions/personal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import {
  setup as setupElevioSnowcat,
  setupUnload as unloadElevioSnowcat,
} from '../../analytics/snowcat';
import { elevioSelectors } from '../../elevio.reducer/selectors';
import { KnowledgeContent } from '../KnowledgeContent';
import { KnowledgeErrorPlaceholder } from '../KnowledgeErrorPlaceholder/KnowledgeErrorPlaceholder';

import { LanguagePickerDropdown } from './ElevioLanguagePickerDropdown';

const avatar = (
  <Avatar
    icon="elevio"
    color={{
      backgroundColor: colors.neutral['200'],
      contentColor: typography.color['tertiary'],
    }}
  />
);

type ElevioCardProps = {
  isMinimized?: boolean;
};

export function ElevioCard({ isMinimized }: ElevioCardProps): JSX.Element {
  const dispatch = useAppDispatch();

  // Set up event tracking for feedback component
  useEffect(() => {
    setupElevioSnowcat();
    return () => unloadElevioSnowcat(); // flush buffer if component unmounts
  }, []);

  const elevioCardOpen = useAppSelector(
    (state) => state.settings.personal.elevioCardOpen,
  );
  const languages = useAppSelector(elevioSelectors.suggestion.languages);
  const errorMessage = useAppSelector(elevioSelectors.suggestion.errorMessage);
  const {
    selectedLanguage: { nativeName: selectedLanguageNativeName },
  } = languages;

  if (isMinimized) {
    return <div className="icon-holder">{avatar}</div>;
  }

  const onToggle = (isOpen: boolean): void => {
    dispatch(
      personalActions.personalToggleRememberElevioCardOpen({
        elevioCardOpen: isOpen,
      }),
    );
  };

  return (
    <CollapseCard onToggle={onToggle} isOpen={elevioCardOpen}>
      <CollapseCard.Header
        dropdown={<LanguagePickerDropdown />}
        dropdownTrigger={selectedLanguageNativeName}
        avatar={avatar}
        slotAfterTitle={<VisibilityToggle cardId="elevio" />}
      >
        Elevio
      </CollapseCard.Header>
      <CollapseCard.Content>
        <ErrorBoundary
          fallback={<KnowledgeErrorPlaceholder message={errorMessage} />}
        >
          <KnowledgeContent />
        </ErrorBoundary>
      </CollapseCard.Content>
    </CollapseCard>
  );
}
